import { cn } from "@/lib/utils";
import getIconFromName from "@/utils/functions/getIconfromName";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { type VariantColors } from "@/types/Factoid";
import Tooltip from "@/components/Tooltip";
import Info from "@/components/icons/Info";

interface WebElementProps {
  type: ElementType;
  htmlId: string;
  name: string;
  location: string;
  position: string;
}

interface IconTextListItemProps {
  icon?: string;
  header: string;
  boldHeader?: boolean;
  description: string;
  small?: boolean;
  className?: string;
  link?: string;
  textContainerClassName?: string;
  headerClassName?: string;
  descriptionClassName?: string;
  iconSize?: string;
  linkClassName?: string;
  webElementProps?: WebElementProps;
  variantColor?: VariantColors;
  tooltip?: string | null;
  iconClassName?: string;
}

export default function IconTextListItem({
  icon,
  header,
  boldHeader = false,
  description,
  small = false,
  className,
  link,
  textContainerClassName,
  headerClassName,
  descriptionClassName,
  iconSize = "48",
  linkClassName,
  webElementProps,
  variantColor = "default",
  tooltip,
  iconClassName,
}: IconTextListItemProps) {
  const webElement: ElementClicked["webElement"] | null = webElementProps
    ? {
        elementType: webElementProps.type || ElementType.Link,
        htmlId: webElementProps.htmlId,
        location: webElementProps.location,
        position: webElementProps.position,
        name: webElementProps.name,
        text: description,
      }
    : null;

  const { elementClicked } = useElementEvents(webElement ? { webElement } : {});

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (webElement) elementClicked();

    const stickyNavHeight = 150;
    window.location.href = e.currentTarget.href;
    setTimeout(() => {
      const target = document.querySelector(":target");
      if (target) {
        const targetPosition =
          target.getBoundingClientRect().top + window.scrollY - stickyNavHeight;
        window.scrollTo({ top: targetPosition, behavior: "smooth" });
      }
    }, 100);
  };

  const variantStyles = {
    default: {
      container: `text-primary`,
      textContainer: `${tooltip ? "" : "md:pr-12"} ${textContainerClassName}`,
    },
    primary: {
      container: `text-white bg-primary-500 py-2 px-4 rounded-[8px] items-center`,
      textContainer: `${textContainerClassName}`,
    },
  };

  return (
    <div
      className={cn(
        "md:text-left flex-1 min-w-0 pb-4 md:pb-0 relative",
        className,
      )}
    >
      <div
        className={cn(
          `flex space-x-4 ${description ? "items-start" : "items-center"}`,
          variantStyles[variantColor].container,
        )}
      >
        {icon && (
          <div
            className={`w-[${iconSize}px] h-[${iconSize}px] ${iconClassName}`}
          >
            {getIconFromName(icon, iconSize)}
          </div>
        )}
        <div className={variantStyles[variantColor].textContainer}>
          <div
            className={`mt-0 md:-md-1 ${small ? cn("text-base mb-0 leading-[1.5rem]", headerClassName) : "text-lg md:mb-2"} ${boldHeader ? "font-bold" : ""}`}
          >
            {header}
            {tooltip && (
              <span className="inline-block ml-2">
                <Tooltip content={tooltip}>
                  <Info
                    svgProps={{
                      width: "16",
                      height: "16",
                      className:
                        "text-gray-500 hover:text-gray-700 cursor-help",
                    }}
                  />
                </Tooltip>
              </span>
            )}
          </div>
          {description && (
            <span className={cn("mb-4 md:mb-0 text-sm", descriptionClassName)}>
              {link ? (
                <a
                  className={cn("underline cursor-pointer", linkClassName)}
                  href={link}
                  onClick={handleClick}
                >
                  {description}
                </a>
              ) : (
                description
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
